'use client';
import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, Lock, Wifi, CheckCircle2 } from 'lucide-react';
import ToastProvider from '@/components/ui/ToastProvider';
import Icon from '@/components/ui/AppIcon';


export default function SignUpLoginContent() {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [accountRestricted, setAccountRestricted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setAccountRestricted(params.get('error') === 'account_suspended');
  }, []);

  return (
    <div className="min-h-screen flex bg-background">
      <ToastProvider />
      {/* Left Panel — hidden on mobile */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[560px] bg-primary p-10 relative overflow-hidden flex-shrink-0">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-white -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-accent translate-x-1/3 translate-y-1/3" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={40} />
            <span className="text-2xl font-800 text-white tracking-tight">Capital City BNT</span>
          </div>

          <h1 className="text-4xl font-800 text-white leading-tight mb-4">
            Banking that works<br />
            <span className="text-accent">as hard as you do.</span>
          </h1>
          <p className="text-white/60 text-base leading-relaxed">
            Checking, savings, wire transfers, and bill pay — all in one secure platform built for modern banking needs.
          </p>

          <div className="mt-10 space-y-4">
            {[
              { icon: Shield, text: 'Member FDIC · Deposits insured up to applicable limits' },
              { icon: Lock, text: 'Account and transaction management' },
              { icon: Wifi, text: 'Real-time transaction alerts and notifications' },
              { icon: CheckCircle2, text: 'View your accounts in one place' },
            ]?.map((item) => {
              const Icon = item?.icon;
              return (
                <div key={`trust-${item?.text?.slice(0, 20)}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={15} className="text-accent" />
                  </div>
                  <span className="text-sm text-white/70">{item?.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-2 p-3 bg-white/8 rounded-xl border border-white/10">
            <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <Shield size={16} className="text-accent" />
            </div>
            <div>
              <p className="text-xs font-600 text-white">Member FDIC</p>
              <p className="text-[11px] text-white/50">Deposit insurance subject to FDIC rules and limits</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-start sm:items-center justify-center p-4 sm:p-6 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-[440px] py-4 sm:py-0">
          {/* Mobile header */}
          <div className="lg:hidden mb-6 sm:mb-8">
            <div className="flex items-center gap-2 mb-4">
              <AppLogo size={32} />
              <span className="text-xl font-800 text-primary">Capital City BNT</span>
            </div>
            {/* Mobile trust badge */}
            <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-xl border border-primary/10">
              <Shield size={14} className="text-primary flex-shrink-0" />
              <p className="text-xs text-muted-foreground">Secure banking platform · Private access</p>
            </div>
          </div>

          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-700 text-foreground">
              {activeTab === 'login' ? 'Welcome back' : 'Open your account'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {activeTab === 'login' ?'Sign in to access your accounts and banking services.' :'Join Capital City BNT and start banking smarter today.'}
            </p>
          </div>

          {accountRestricted && (
            <div role="alert" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4">
              <h3 className="text-sm font-700 text-amber-900">
                Account Access Restricted
              </h3>
              <p className="mt-2 text-sm text-amber-800">
                Your account is currently suspended or restricted.
                Please contact customer support for assistance.
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex bg-muted rounded-xl p-1 mb-5 sm:mb-6">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-2.5 sm:py-2 text-sm font-600 rounded-lg transition-all duration-150 ${
                activeTab === 'login' ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('signup')}
              className={`flex-1 py-2.5 sm:py-2 text-sm font-600 rounded-lg transition-all duration-150 ${
                activeTab === 'signup' ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Open Account
            </button>
          </div>

          {activeTab === 'login' ? (
            <LoginForm />
          ) : (
            <SignUpForm onSuccess={() => setActiveTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}